import { useMemo, useState } from "react";
import PageHeader from "@/components/base/PageHeader";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import NewsCard from "@/components/feature/NewsCard";
import { allNews, paginate, totalPages } from "@/utils/content";

const PAGE_SIZE = 4;
const categories = ["全部", "官方动态", "粉丝活动", "剧情解析", "角色分析", "周边情报", "制作幕后"];

export default function News() {
  const [category, setCategory] = useState("全部");
  const [sort, setSort] = useState<"newest" | "oldest">("newest");
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list = allNews.filter((n) => category === "全部" || n.category === category);
    return [...list].sort((a, b) => {
      const diff = new Date(a.date).getTime() - new Date(b.date).getTime();
      return sort === "newest" ? -diff : diff;
    });
  }, [category, sort]);

  const pageCount = totalPages(filtered.length, PAGE_SIZE);
  const current = Math.min(page, pageCount);
  const visible = paginate(filtered, current, PAGE_SIZE);

  const resetFilters = () => {
    setCategory("全部");
    setSort("newest");
    setPage(1);
  };

  return (
    <div>
      <PageHeader
        eyebrow="资讯动态"
        icon="ri-newspaper-line"
        title="火影忍者最新资讯"
        description="汇聚动漫相关的官方动态、角色分析、周边情报与粉丝活动，及时了解忍者世界的最新消息。"
      />

      <div className="mx-auto w-full max-w-7xl px-4 py-8 md:px-6 md:py-10">
        <div className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-4 md:flex-row md:items-center md:justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((item) => (
              <button
                key={item}
                type="button"
                onClick={() => {
                  setCategory(item);
                  setPage(1);
                }}
                className={`rounded-full px-3.5 py-1.5 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  category === item
                    ? "bg-primary-500 text-background-50"
                    : "bg-background-50 text-foreground-600 hover:bg-background-200"
                }`}
              >
                {item}
              </button>
            ))}
          </div>

          <label className="flex items-center gap-2 text-sm text-foreground-600">
            <i className="ri-time-line" />
            <select
              value={sort}
              onChange={(e) => {
                setSort(e.target.value as "newest" | "oldest");
                setPage(1);
              }}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none focus:border-primary-400 cursor-pointer"
            >
              <option value="newest">按时间从新到旧</option>
              <option value="oldest">按时间从旧到新</option>
            </select>
          </label>
        </div>

        <p className="mt-5 text-sm text-foreground-500">
          共找到 <strong className="text-foreground-900">{filtered.length}</strong> 条资讯
          {category !== "全部" && ` · 分类「${category}」`}
        </p>

        {visible.length === 0 ? (
          <div className="mt-5">
            <EmptyState
              title="暂无相关资讯"
              description="当前分类下没有资讯，试试切换其它分类。"
              onReset={resetFilters}
            />
          </div>
        ) : (
          <>
            <div className="mt-5 space-y-5">
              {visible.map((item) => (
                <NewsCard key={item.id} item={item} />
              ))}
            </div>
            <Pagination page={current} total={pageCount} onChange={setPage} />
          </>
        )}
      </div>
    </div>
  );
}